import { useQuery } from "@tanstack/react-query"

interface DocPage {
  path: string
  title: string
  markdown: string
  order: number
  icon?: string
  label?: string
}

interface TreeItem {
  title: string
  path: string
  order: number
  icon?: string
  label?: string
  expanded?: boolean
  children?: TreeItem[]
}

interface DocsManifest {
  versions: string[]
  latest: string | null
  generatedAt: string
}

interface DocsIndex {
  version: string
  pages: Record<string, DocPage>
}

interface DocsTree {
  items: TreeItem[]
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.statusText}`)
  }
  return response.json()
}

export function useDocsManifest() {
  return useQuery({
    queryKey: ["docs-manifest"],
    queryFn: () => fetchJson<DocsManifest>("/docs-versions/manifest.json"),
    staleTime: 1000 * 60 * 5,
  })
}

export function useDocsIndex(version: string | undefined) {
  return useQuery({
    queryKey: ["docs-index", version],
    queryFn: () => fetchJson<DocsIndex>(`/docs-versions/${version}/index.json`),
    enabled: !!version,
    staleTime: 1000 * 60 * 5,
  })
}

export function useDocsTree(version: string | undefined) {
  return useQuery({
    queryKey: ["docs-tree", version],
    queryFn: () => fetchJson<DocsTree>(`/docs-versions/${version}/tree.json`),
    enabled: !!version,
    staleTime: 1000 * 60 * 5,
  })
}

interface SearchIndexData {
  serializedIndex: object
  docs: Record<string, { title: string; content: string }>
}

export function useDocsSearchData(version: string | undefined) {
  return useQuery({
    queryKey: ["docs-search", version],
    queryFn: async (): Promise<SearchIndexData> => {
      const [indexRes, docsRes] = await Promise.all([
        fetch(`/docs-versions/${version}/search-index.json`),
        fetch(`/docs-versions/${version}/search-docs.json`),
      ])
      if (!indexRes.ok) {
        throw new Error(`Failed to fetch search index: ${indexRes.statusText}`)
      }
      if (!docsRes.ok) {
        throw new Error(`Failed to fetch search docs: ${docsRes.statusText}`)
      }
      const [serializedIndex, docs] = await Promise.all([
        indexRes.json(),
        docsRes.json(),
      ])
      return { serializedIndex, docs }
    },
    enabled: !!version,
    staleTime: 1000 * 60 * 5,
  })
}

export function useDocs(version: string | undefined, pagePath: string) {
  const indexQuery = useDocsIndex(version)
  const treeQuery = useDocsTree(version)

  const page = indexQuery.data?.pages[pagePath]
  const tree = treeQuery.data?.items

  return {
    page,
    tree,
    isLoading: indexQuery.isLoading || treeQuery.isLoading,
    error: indexQuery.error || treeQuery.error,
  }
}

export type {
  DocPage,
  TreeItem,
  DocsManifest,
  DocsIndex,
  DocsTree,
  SearchIndexData,
}
